import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import React from "react";

const BUGSNAG_API_KEY = import.meta.env.VITE_BUGSNAG_API_KEY || "";

const RELEASE_STAGE = import.meta.env.PROD ? "production" : "development";

const APP_VERSION = import.meta.env.VITE_APP_VERSION || "1.0.0";

// Error patterns to ignore (browser extensions, third-party noise)
const IGNORED_ERROR_PATTERNS: Array<string | RegExp> = [
  // Browser extensions
  /extensions\//i,
  /^chrome-extension:\/\//,
  /^moz-extension:\/\//,
  /^safari-extension:\/\//,

  // Common browser/network errors
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications",

  // Script loading errors (usually ad blockers or extensions)
  "Script error.",
  "Script error",

  // Third-party scripts
  /hotjar/,
  /intercom/,

  // React hydration
  "Hydration failed",
  "Text content does not match",
];

export function initBugsnag() {
  // No-op when API key is missing (local dev without secrets)
  if (!BUGSNAG_API_KEY) {
    return;
  }

  Bugsnag.start({
    apiKey: BUGSNAG_API_KEY,
    plugins: [new BugsnagPluginReact()],
    releaseStage: RELEASE_STAGE,
    appVersion: APP_VERSION,
    enabledReleaseStages: ["production", "staging"],
    onError: (event) => {
      // Check if error matches ignored patterns
      const errorMessage = event.errors?.[0]?.errorMessage || "";
      const isIgnored = IGNORED_ERROR_PATTERNS.some((pattern) => {
        if (typeof pattern === "string") {
          return errorMessage.includes(pattern);
        }
        return pattern.test(errorMessage);
      });

      if (isIgnored) return false;

      // Check if error originated from a browser extension
      const stacktrace = event.errors?.[0]?.stacktrace || [];
      const isFromExtension = stacktrace.some(
        (frame: { file?: string }) =>
          frame.file?.includes("extension://") ||
          frame.file?.includes("extensions/")
      );

      if (isFromExtension) return false;

      return true;
    },
  });
}

/**
 * Get the BugsnagErrorBoundary component.
 * Returns a passthrough fragment when Bugsnag is not started (local dev).
 */
export function getBugsnagErrorBoundary() {
  if (!BUGSNAG_API_KEY) {
    // Return a simple passthrough component when Bugsnag is not initialized
    return ({ children }: { children: React.ReactNode }) =>
      React.createElement(React.Fragment, null, children);
  }

  const plugin = Bugsnag.getPlugin("react");
  if (!plugin) {
    return ({ children }: { children: React.ReactNode }) =>
      React.createElement(React.Fragment, null, children);
  }

  return plugin.createErrorBoundary(React);
}

export default Bugsnag;
